import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TrainingDrill, DrillDifficulty, DrillCategory, AgeGroup } from './Model';
import { restClient } from './RestClient';
import { AuthService } from './AuthService';
import { YouTubeEmbed } from './YouTubeEmbed';
import { useReferenceData } from './ReferenceDataContext';

interface EditDrillProps {
  onError?: (message: string) => void;
}

const EMPTY_DRILL: TrainingDrill = {
  id: 0,
  name: '',
  description: '',
  youtubeUrl: '',
  difficulty: 'Intermediate',
  playersRequired: 4,
  duration: 10,
  skills: [],
  ageGroups: [],
  equipment: {},
  spaceRequired: { width: 25, height: 25 },
  category: 'Main',
};

export const EditDrill: React.FC<EditDrillProps> = ({ onError }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { referenceData, currentSport, currentSportConfig } = useReferenceData();

  const isCreateMode = id === 'new';

  const [drill, setDrill] = useState<TrainingDrill | null>(isCreateMode ? { ...EMPTY_DRILL, sport: currentSport } : null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [fetchingYouTube, setFetchingYouTube] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = AuthService.isAdmin();

  useEffect(() => {
    if (!isAdmin) {
      navigate('/training-drills');
      return;
    }

    if (isCreateMode) {
      setLoading(false);
      return;
    }

    const fetchDrill = async () => {
      if (!id) {
        setError('Invalid drill ID');
        setLoading(false);
        return;
      }

      const result = await restClient.getDrill(parseInt(id, 10));
      if (result.IsSuccess) {
        setDrill(result.Value);
      } else {
        setError(result.Error?.description ?? 'Failed to load drill');
        onError?.(result.Error?.description ?? 'Failed to load drill');
      }
      setLoading(false);
    };

    fetchDrill();
  }, [id, isAdmin, isCreateMode, navigate, onError]);

  const handleFetchYouTube = async () => {
    if (!youtubeUrl.trim()) {
      setError('Please enter a YouTube URL');
      return;
    }

    setFetchingYouTube(true);
    setError(null);

    const result = await restClient.fetchYouTubeDetails(youtubeUrl, currentSport);
    if (result.IsSuccess) {
      const info = result.Value;
      setDrill(prev => prev ? {
        ...prev,
        name: info.title,
        description: info.description,
        youtubeUrl: info.youtubeUrl,
        duration: info.duration,
        difficulty: info.inferredDifficulty,
        category: info.inferredCategory,
        skills: info.inferredSkills,
        ageGroups: info.inferredAgeGroups,
        playersRequired: info.inferredPlayersRequired,
        equipment: info.inferredEquipment,
        sport: currentSport,
      } : null);
    } else {
      setError(result.Error.description ?? 'Failed to fetch YouTube video details');
    }
    setFetchingYouTube(false);
  };

  const handleSave = async () => {
    if (!drill) return;

    if (!drill.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!drill.description.trim()) {
      setError('Description is required');
      return;
    }
    if (drill.skills.length === 0) {
      setError('At least one skill must be selected');
      return;
    }
    if (drill.ageGroups.length === 0) {
      setError('At least one age group must be selected');
      return;
    }
    if (isCreateMode && !drill.youtubeUrl?.trim()) {
      setError('YouTube URL is required. Please fetch video details first.');
      return;
    }

    setSaving(true);
    setError(null);

    if (isCreateMode) {
      const result = await restClient.createDrill(drill);
      if (result.IsSuccess) {
        navigate('/training-drills', { state: { message: 'Drill created successfully!' } });
      } else {
        setError(result.Error?.description ?? 'Failed to create drill');
      }
    } else {
      const result = await restClient.updateDrill(parseInt(id!, 10), drill);
      if (result.IsSuccess) {
        navigate('/training-drills', { state: { message: 'Drill updated successfully!' } });
      } else {
        setError(result.Error?.description ?? 'Failed to save drill');
      }
    }
    setSaving(false);
  };

  const handleCancel = () => {
    navigate('/training-drills');
  };

  const updateField = <K extends keyof TrainingDrill>(field: K, value: TrainingDrill[K]) => {
    if (drill) {
      setDrill({ ...drill, [field]: value });
    }
  };

  const updateEquipment = (field: string, value: number) => {
    if (drill) {
      setDrill({
        ...drill,
        equipment: { ...drill.equipment, [field]: value }
      });
    }
  };

  const updateSpaceRequired = (field: 'width' | 'height', value: number) => {
    if (drill) {
      setDrill({
        ...drill,
        spaceRequired: { ...drill.spaceRequired, [field]: value }
      });
    }
  };

  const toggleSkill = (skill: string) => {
    if (drill) {
      const skills = drill.skills.includes(skill)
        ? drill.skills.filter(s => s !== skill)
        : [...drill.skills, skill];
      setDrill({ ...drill, skills });
    }
  };

  const toggleAgeGroup = (ageGroup: AgeGroup) => {
    if (drill) {
      const ageGroups = drill.ageGroups.includes(ageGroup)
        ? drill.ageGroups.filter(ag => ag !== ageGroup)
        : [...drill.ageGroups, ageGroup];
      setDrill({ ...drill, ageGroups });
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body loading-state">
          <p className="text-muted text-lg m-0">Loading drill...</p>
        </div>
      </div>
    );
  }

  if (!drill) {
    return (
      <div className="card">
        <div className="card-body loading-state">
          <p className="text-danger text-lg m-0">{error || 'Drill not found'}</p>
          <button onClick={() => navigate('/training-drills')} className="btn btn-secondary mt-4">
            Back to Drills
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-800 mx-auto">
      {/* Header */}
      <div className="page-header">
        <h2 className="page-title">{isCreateMode ? 'Add New Drill' : 'Edit Drill'}</h2>
        <p className="page-description">
          {isCreateMode
            ? 'Enter a YouTube URL to fetch video details, then review and save the drill.'
            : 'Modify the drill metadata below. YouTube URL cannot be changed.'}
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-error mb-4">{error}</div>
      )}

      {/* Form */}
      <div className="card">
        <div className="card-body-lg">
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            {/* Name */}
            <div className="form-group">
              <label htmlFor="name" className="form-label">Name *</label>
              <input
                type="text"
                id="name"
                className="form-control"
                value={drill.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
                maxLength={100}
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="description" className="form-label">Description *</label>
              <textarea
                id="description"
                className="form-control"
                value={drill.description}
                onChange={(e) => updateField('description', e.target.value)}
                required
              />
            </div>

            {/* YouTube URL / Video */}
            <div className="form-group">
              <label htmlFor="youtubeUrl" className="form-label">
                {isCreateMode ? 'YouTube URL *' : 'Video Reference'}
              </label>
              {isCreateMode ? (
                <>
                  <div className="input-group">
                    <input
                      type="text"
                      id="youtubeUrl"
                      className="form-control"
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                    <button
                      type="button"
                      onClick={handleFetchYouTube}
                      disabled={fetchingYouTube || !youtubeUrl.trim()}
                      className={`btn ${fetchingYouTube || !youtubeUrl.trim() ? 'btn-secondary' : 'btn-info'}`}
                    >
                      {fetchingYouTube ? 'Fetching...' : 'Fetch Details'}
                    </button>
                  </div>
                  <p className="form-help">Enter the YouTube URL and click "Fetch Details" to populate the form.</p>
                  {drill.youtubeUrl && (
                    <div className="mt-3">
                      <YouTubeEmbed url={drill.youtubeUrl} title={drill.name || 'Video preview'} borderRadius={4} />
                    </div>
                  )}
                </>
              ) : drill.youtubeUrl ? (
                <YouTubeEmbed url={drill.youtubeUrl} title={`Video for ${drill.name}`} borderRadius={4} />
              ) : (
                <div className="card loading-state">
                  <p className="text-muted m-0">No video available for this drill</p>
                </div>
              )}
            </div>

            {/* Difficulty and Category in a row */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="difficulty" className="form-label">Difficulty *</label>
                <select
                  id="difficulty"
                  className="form-select"
                  value={drill.difficulty}
                  onChange={(e) => updateField('difficulty', e.target.value as DrillDifficulty)}
                  required
                >
                  {(referenceData?.difficulties ?? []).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="category" className="form-label">Category *</label>
                <select
                  id="category"
                  className="form-select"
                  value={drill.category}
                  onChange={(e) => updateField('category', e.target.value as DrillCategory)}
                  required
                >
                  {(referenceData?.categories ?? []).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Duration and Players in a row */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="duration" className="form-label">Duration (minutes) *</label>
                <input
                  type="number"
                  id="duration"
                  className="form-control"
                  value={drill.duration}
                  onChange={(e) => updateField('duration', parseInt(e.target.value) || 0)}
                  required
                  min={1}
                  max={120}
                />
              </div>

              <div>
                <label htmlFor="playersRequired" className="form-label">Players Required *</label>
                <input
                  type="number"
                  id="playersRequired"
                  className="form-control"
                  value={drill.playersRequired}
                  onChange={(e) => updateField('playersRequired', parseInt(e.target.value) || 1)}
                  required
                  min={1}
                  max={30}
                />
              </div>
            </div>

            {/* Skills - Multi-select with checkboxes */}
            <div className="form-group">
              <label className="form-label">Skills *</label>
              <div className="form-check-grid">
                {(currentSportConfig?.skills ?? referenceData?.skills ?? []).map(skill => (
                  <label key={skill} className="form-check">
                    <input
                      type="checkbox"
                      checked={drill.skills.includes(skill)}
                      onChange={() => toggleSkill(skill)}
                    />
                    <span>{skill}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Age Groups - Multi-select with checkboxes */}
            <div className="form-group">
              <label className="form-label">Age Groups *</label>
              <div className="form-check-grid form-check-grid-wide">
                {(referenceData?.ageGroups ?? []).map(({ value, label }) => (
                  <label key={value} className="form-check">
                    <input
                      type="checkbox"
                      checked={drill.ageGroups.includes(value)}
                      onChange={() => toggleAgeGroup(value)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Equipment Section */}
            <fieldset className="form-fieldset">
              <legend>Equipment</legend>
              <div className="equipment-grid">
                {(currentSportConfig?.equipmentOptions ?? []).map(item => {
                  const fieldId = `equipment-${item.toLowerCase().replace(/\s+/g, '-')}`;
                  return (
                    <div key={item}>
                      <label htmlFor={fieldId} className="form-label">{item}</label>
                      <input
                        type="number"
                        id={fieldId}
                        className="form-control"
                        value={drill.equipment[item] || 0}
                        onChange={(e) => updateEquipment(item, parseInt(e.target.value) || 0)}
                        min={0}
                      />
                    </div>
                  );
                })}
              </div>
            </fieldset>

            {/* Space Required */}
            <fieldset className="form-fieldset">
              <legend>Space Required (meters)</legend>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="spaceWidth" className="form-label">Width</label>
                  <input
                    type="number"
                    id="spaceWidth"
                    className="form-control"
                    value={drill.spaceRequired.width || 0}
                    onChange={(e) => updateSpaceRequired('width', parseInt(e.target.value) || 0)}
                    min={1}
                    max={200}
                  />
                </div>

                <div>
                  <label htmlFor="spaceHeight" className="form-label">Height</label>
                  <input
                    type="number"
                    id="spaceHeight"
                    className="form-control"
                    value={drill.spaceRequired.height || 0}
                    onChange={(e) => updateSpaceRequired('height', parseInt(e.target.value) || 0)}
                    min={1}
                    max={200}
                  />
                </div>
              </div>
            </fieldset>

            {/* Hidden Toggle (edit mode only) */}
            {!isCreateMode && (
              <div className="form-group">
                <label className="form-check">
                  <input
                    type="checkbox"
                    checked={drill.isHidden ?? false}
                    onChange={(e) => updateField('isHidden', e.target.checked)}
                  />
                  <span>Hidden</span>
                </label>
                <p className="form-help">Hidden drills are excluded from session plans and the drills library.</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                type="submit"
                disabled={saving}
                className={`btn ${saving ? 'btn-secondary' : 'btn-primary'}`}
              >
                {saving ? 'Saving...' : (isCreateMode ? 'Create Drill' : 'Save Changes')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
